import { useEffect, useState } from 'react';
import { fetchFactionProfiles, type FactionProfileView } from '../api/catalog';
import type { Definition } from '../api/missions';
import { ca } from '../i18n/ca';
import { FactionEmblem } from './FactionEmblem';
import { factionLabel } from './factionNames';

/**
 * Who fights whom, with each side's flag: the players' faction against the
 * enemy's. The definition carries the classes; the names and flags come from
 * the mod set's own faction profiles, and a class the catalogue does not know
 * still gets its drawn emblem and its class as a name.
 */
export function MissionSides({
  sides,
  modSetHash,
}: {
  sides: Definition['sides'];
  modSetHash: string | null;
}) {
  const [profiles, setProfiles] = useState<FactionProfileView[]>([]);
  useEffect(() => {
    if (!modSetHash) return undefined;
    let live = true;
    fetchFactionProfiles(modSetHash)
      .then((p) => live && setProfiles(p))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [modSetHash]);

  const one = (label: string, side: string, factionClass: string, flag?: string) => {
    const profile = profiles.find((p) => p.factionClass === factionClass);
    const name = profile ? factionLabel(profile) : factionClass;
    return (
      <div className="mission-side">
        <FactionEmblem
          name={name}
          factionClass={factionClass}
          side={side}
          size={48}
          picture={flag || profile?.picture || null}
        />
        <div>
          <p className="muted small">{label}</p>
          <p>
            <strong>{name}</strong>
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="mission-sides" aria-label={ca.missionUi.page.sides}>
      {one(ca.missionUi.page.ours, sides.players.side, sides.players.faction, sides.players.flag)}
      <span className="versus" aria-hidden="true">
        vs
      </span>
      {one(ca.missionUi.page.theirs, sides.enemy.side, sides.enemy.faction)}
    </div>
  );
}
